import { useNavigate, useLocation } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useExitIntent } from "@/hooks/useExitIntent";
import { getPopupConfigForRoute, PopupConfig } from "@/data/exitIntentPopupConfig";

const ExitIntentPopup = () => {
  const { showPopup, closePopup } = useExitIntent();
  const location = useLocation();
  const navigate = useNavigate();

  // Get the appropriate popup config based on current route
  const config: PopupConfig = getPopupConfigForRoute(location.pathname);
  const IconComponent = config.icon;

  if (!showPopup) return null;

  const handleCTAClick = () => {
    closePopup();
    navigate(config.funnelUrl);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground/60 backdrop-blur-sm animate-fade-in"
        onClick={closePopup}
      />

      {/* Modal */}
      <div className="relative bg-background rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in border border-border">
        {/* Close button */}
        <button
          onClick={closePopup}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors z-10"
          aria-label="Close popup"
        >
          <X className="h-5 w-5 text-muted-foreground" />
        </button>

        {/* Header gradient */}
        <div className="bg-primary px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-accent/20 mb-4">
            <IconComponent className="h-8 w-8 text-accent" />
          </div>
          <h2 className="text-2xl font-bold text-primary-foreground mb-2">
            {config.headline}
          </h2>
          <p className="text-primary-foreground/80">
            {config.subheadline}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-3 mb-6">
            {config.benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 text-sm text-foreground">
                <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-accent">{index + 1}</span>
                </div>
                <span>{benefit}</span>
              </div>
            ))}
          </div>

          <Button
            onClick={handleCTAClick}
            className="w-full"
            size="lg"
          >
            {config.ctaText}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>

          <button
            onClick={closePopup}
            className="w-full mt-3 text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
          >
            No thanks, I'm not interested
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
